import { TAdverseMediaStatus } from './types';
import { ascOrderRecords, latestRecord } from './utils';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgressButton from '../Form/CircularProgressButton';
import FormSwitch from '../Form/FormSwitch';
import TextEditor from '../TextEditor';
import { useCustomForm } from '../Form/useCustomForm';
import tokens from '@verifime/design-tokens';
import { formatToDDMMMYYYYHHMMAAA, validationRules } from '@verifime/utils';
import React from 'react';
import { SubmitHandler } from 'react-hook-form';

const commentTextFieldName: keyof TAdverseMediaStatus = 'commentText';
const adverseMediaStatusFieldName: keyof TAdverseMediaStatus = 'adverseMediaStatus';

export type TAdverseMediaEditorProps = {
  data?: Partial<TAdverseMediaStatus>[];
  onSubmitNewAdverseMediaStatus: SubmitHandler<TAdverseMediaStatus>;
};

/**
 * AdverseMediaEditor allows users to view existing adverse media records and add new ones.
 *
 * The component displays a toggle for adverse media status, shows historical records in chronological order,
 * and provides an interface to add new records with comments. Each record displays the author, timestamp,
 * and comment text (which may contain HTML links).
 *
 * Key features:
 * - Toggle switch for adverse media match status
 * - Historical records display with metadata
 * - Expandable text editor for adding new records
 * - Form validation for required comment text
 * - Automatic form reset after submission
 */
export default function AdverseMediaEditor({
  data = [],
  onSubmitNewAdverseMediaStatus,
}: Readonly<TAdverseMediaEditorProps>) {
  const orderedData = ascOrderRecords(data);

  const {
    handleSubmit,
    control,
    reset,
    trigger,
    setValue,
    clearErrors,
    formState: { errors },
  } = useCustomForm<TAdverseMediaStatus>({
    schema: {
      [commentTextFieldName]: validationRules.requiredStringRule({
        requiredMessage: 'Cannot be empty',
      }),
    },
  });
  const [isShowEditor, setIsShowEditor] = React.useState(false);

  React.useEffect(() => {
    if (data.length === 0) {
      reset({ [adverseMediaStatusFieldName]: false });
      return;
    }

    const latest = latestRecord(data);
    reset({
      [adverseMediaStatusFieldName]: latest?.adverseMediaStatus,
    });
  }, [data, reset]);

  const onSubmit: SubmitHandler<TAdverseMediaStatus> = async (record) => {
    try {
      await onSubmitNewAdverseMediaStatus?.(record);
    } finally {
      setIsShowEditor(false);
      setValue(commentTextFieldName, '');
    }
  };

  return (
    <Paper
      sx={{ padding: tokens.spacingBase, width: '100%' }}
      data-testid="adverse-media-editor"
      data-component="adverse-media-editor"
    >
      <Stack gap={tokens.spacingXs} alignItems="start">
        <Box data-testid="adverse-media-toggle" data-field="adverse-media-status">
          <FormSwitch
            name={adverseMediaStatusFieldName}
            label="Adverse Media Match found"
            defaultChecked={false}
            control={control}
            color="error"
          />
        </Box>

        {orderedData.map((record, index) => (
          <Stack
            key={record.id}
            data-testid={`adverse-media-record-${index}`}
            data-record-id={record.id || `record-${index}`}
            data-record-status={record.adverseMediaStatus ? 'positive' : 'negative'}
            data-record-type="adverse-media"
            data-updated-by={record.lastUpdatedBy}
            data-updated-date={record.lastUpdatedDatetime}
          >
            <Typography variant="h6">{record.lastUpdatedBy}</Typography>
            <Typography color="textSecondary">
              {formatToDDMMMYYYYHHMMAAA(record.lastUpdatedDatetime)}
            </Typography>
            {/* commentText has already been sanitized by the backend.  */}
            <Typography
              dangerouslySetInnerHTML={{ __html: record.commentText }}
              data-testid={`adverse-media-comment-${index}`}
            />
          </Stack>
        ))}

        {isShowEditor ? (
          <Stack sx={{ width: '100%', gap: tokens.spacingXs }}>
            <Box data-testid="adverse-media-text-editor" data-field="comment-text">
              <TextEditor
                placeholder="Add note to explain the status"
                onTextChange={(text) => {
                  setValue(commentTextFieldName, text);
                  trigger(commentTextFieldName);
                }}
                errorMessage={(errors[commentTextFieldName]?.message as string) ?? ''}
              />
            </Box>
            <Stack direction="row" justifyContent="end" gap={tokens.spacingXs}>
              <Button
                variant="outlined"
                onClick={() => {
                  setIsShowEditor(false);
                  setValue(commentTextFieldName, '');
                  clearErrors(commentTextFieldName);
                }}
                data-testid="adverse-media-cancel-btn"
                data-action="cancel-edit"
              >
                Cancel
              </Button>
              <CircularProgressButton
                variant="contained"
                onClick={handleSubmit(onSubmit)}
                data-testid="adverse-media-submit-btn"
                data-action="submit-record"
              >
                Done
              </CircularProgressButton>
            </Stack>
          </Stack>
        ) : (
          <Button
            variant="outlined"
            onClick={() => setIsShowEditor(true)}
            data-testid="adverse-media-add-btn"
            data-action="add-note"
          >
            Add Note
          </Button>
        )}
      </Stack>
    </Paper>
  );
}
